import React, { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "../supabase/supabase";
import { getAppVersion, isUpdateRequired } from "../utils/appVersion";

/**
 * Blocks a native build that is below the minimum version in config.app_gate.
 *
 * The gate only governs builds that already contain it, so it can never rescue
 * an older one. That is the whole reason it ships early rather than when it is
 * first needed.
 *
 * Native only. The web app always serves the newest bundle on the next load, so
 * there is nothing there to force. That also means a build with no version
 * baked in (any local `npm start`) is never gated.
 *
 * The row is read on mount and again when the app returns to the foreground, so
 * a floor raised mid-session takes effect on the next resume rather than
 * waiting for a cold launch.
 */

// Reachable even while blocked. App Review opens these directly, and a user who
// wants out rather than an update needs the deletion page to stay available.
const ALWAYS_ALLOWED = ["/privacy", "/terms", "/support", "/delete-account"];

const storeUrlFor = (gate) => {
  if (!gate) return null;
  const url = Capacitor.getPlatform() === "android" ? gate.androidUrl : gate.iosUrl;
  return typeof url === "string" && url.startsWith("https://") ? url : null;
};

const UpdateGate = ({ children }) => {
  const [gate, setGate] = useState(null);

  const load = useCallback(async () => {
    // maybeSingle so a missing row resolves to null instead of throwing. A gate
    // that cannot read its own configuration lets the app through.
    const { data, error } = await supabase
      .from("config")
      .select("value")
      .eq("key", "app_gate")
      .maybeSingle();

    if (error) return;
    setGate(data?.value || null);
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    load();

    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const blocked =
    Capacitor.isNativePlatform() &&
    !ALWAYS_ALLOWED.includes(window.location.pathname) &&
    isUpdateRequired({ version: getAppVersion(), minVersion: gate?.minVersion });

  if (!blocked) return children;

  const storeUrl = storeUrlFor(gate);
  const message =
    typeof gate.message === "string" && gate.message.trim()
      ? gate.message
      : "This version of Lineup is out of date and can no longer be used. Update to the latest version to carry on.";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 max-w-sm w-full text-center">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Update required</h1>
        <p className="text-sm text-gray-500 mb-5">{message}</p>

        {storeUrl ? (
          <a
            href={storeUrl}
            target="_blank"
            rel="noreferrer"
            className="block w-full bg-blue-600 text-white font-semibold rounded-xl py-3"
          >
            Update now
          </a>
        ) : (
          <p className="text-sm text-gray-500">
            Open the App Store and update Lineup, then come back.
          </p>
        )}
      </div>
    </div>
  );
};

export default UpdateGate;
